import React, { ReactNode, useState } from "react";
import moment, { Moment } from "moment";
import {
  SxProps,
  Theme,
  useMediaQuery,
  WhmButton,
  WhmDatePicker,
  WhmDatePickerProps,
  WhmStack,
  WhmTextField,
} from "@securitize/reactjs-whm";
import { textFieldStyle } from "./styles";
import { Translate } from "../../Translation/Translate";
import TranslationKeys from "../../../contexts/I18nContext/translationKeys";

interface DatePickerInputProps
  extends Omit<
    WhmDatePickerProps<Moment, Moment>,
    "value" | "onChange" | "onOpen" | "onClose" | "renderInput" | "InputProps"
  > {
  dataTestId: string;
  name: string;
  label: ReactNode;
  value: Moment | null;
  onChange: (e: { target: { name: string; value: Moment | null } }) => void;
  onBlur?: (e?: React.FocusEvent<any>) => void;
  error?: boolean;
  helperText?: string | boolean;
  language: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

function DatePickerInput({
  dataTestId,
  name,
  onChange,
  onBlur,
  error,
  helperText,
  language,
  placeholder,
  sx,
  ...props
}: DatePickerInputProps) {
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const isMobileOrTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xl"),
  );
  // This shouldn't be needed but fixes an issue with the date picker which
  // would not refresh the days of the week names when changing language
  moment.locale(language);
  moment.updateLocale(language, {
    week: {
      // First day Sunday
      dow: language === "en" ? 0 : 1,
    },
  });

  const handleDateChange = (date: Moment | null) => {
    const event = {
      target: {
        name: name,
        value: date,
      },
    };
    onChange(event);
  };

  return (
    <WhmDatePicker
      {...props}
      open={openPicker}
      onOpen={() => setOpenPicker(true)}
      onClose={() => {
        setOpenPicker(false);
      }}
      onChange={handleDateChange}
      DialogProps={{
        sx: {
          "& .MuiDialogContent-root": {
            padding: "6px !important",
          },
        },
      }}
      components={
        isMobileOrTablet
          ? {
              ActionBar: (actionBarProps) => (
                <WhmStack
                  direction="row"
                  gap="8px"
                  padding="8px"
                  justifyContent="end"
                >
                  <WhmButton
                    onClick={actionBarProps.onCancel}
                    color={"secondary"}
                    variant="outlined"
                  >
                    <Translate
                      translationKey={
                        TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_CANCEL
                      }
                    />
                  </WhmButton>
                  <WhmButton
                    onClick={actionBarProps.onAccept}
                    variant="contained"
                  >
                    <Translate
                      translationKey={
                        TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_CONFIRM
                      }
                    />
                  </WhmButton>
                </WhmStack>
              ),
            }
          : undefined
      }
      renderInput={(textInputProps) => (
        <WhmTextField
          {...textInputProps}
          name={name}
          helperText={helperText}
          error={error}
          autoFocus={false}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          sx={[textFieldStyle, ...(Array.isArray(sx) ? sx : [sx || {}])]}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete="off"
          inputProps={{
            ...textInputProps.inputProps,
            "data-test-id": dataTestId,
          }}
        />
      )}
    />
  );
}

export default DatePickerInput;
